type ThemePreference = 'auto' | 'light' | 'dark';

type SortKey = 'updated_at' | 'name' | 'price_in' | 'price_out' | 'context';

interface ModelPricing {
  currency: string;
  input: number | null;
  output: number | null;
}

interface ModelBenchmark {
  name: string;
  score: number;
  unit?: string;
  higherIsBetter?: boolean;
  source?: string;
}

interface ModelLinks {
  provider?: string;
  docs?: string;
  license?: string;
  pricing?: string;
}

interface ModelRecord {
  id: string;
  name: string;
  provider: string;
  description: string;
  tags: string[];
  modalities: string[];
  features: string[];
  pricing: ModelPricing;
  contextWindow: number;
  updatedAt: string;
  releaseDate?: string;
  metrics?: Record<string, number | string>;
  benchmarks?: ModelBenchmark[];
  links?: ModelLinks;
}

interface AppState {
  models: ModelRecord[];
  loading: boolean;
  error: string | null;
}

type Route =
  | { name: 'list'; params: URLSearchParams }
  | { name: 'detail'; id: string }
  | { name: 'compare'; ids: string[]; params: URLSearchParams }
  | { name: 'about' };

const STATE: AppState = {
  models: [],
  loading: true,
  error: null,
};

const DATA_URL = './data/models.json';
const PAGE_SIZE = 24;
const THEME_STORAGE_KEY = 'aimarket-theme';
const THEME_ORDER: ThemePreference[] = ['auto', 'light', 'dark'];

const appRootElement = document.getElementById('app');
const mainRegionElement = document.getElementById('main');
const yearEl = document.getElementById('year');
const themeToggleButton = document.getElementById('theme-toggle') as HTMLButtonElement | null;
const systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');

let currentTheme: ThemePreference = 'auto';

if (!(appRootElement instanceof HTMLElement) || !(mainRegionElement instanceof HTMLElement)) {
  throw new Error('Application container missing in DOM');
}

const appRoot = appRootElement;
const mainRegion = mainRegionElement;

if (yearEl) {
  yearEl.textContent = new Date().getFullYear().toString();
}

initThemeControls();
ensureDefaultRoute();
void loadModels();
window.addEventListener('hashchange', handleRouteChange);
render();

function ensureDefaultRoute(): void {
  if (!window.location.hash || window.location.hash === '#') {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/models`);
  }
}

function parseRoute(): Route {
  const raw = window.location.hash.trim() || '#/models';
  const trimmed = raw.replace(/^#/, '');
  const normalized = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  const [pathPart, queryPart = ''] = normalized.split('?');
  const segments = pathPart.split('/').filter(Boolean);
  const params = new URLSearchParams(queryPart);

  if (segments.length === 0 || segments[0] === 'models' && segments.length === 1) {
    return { name: 'list', params };
  }

  if (segments[0] === 'models' && segments[1]) {
    return { name: 'detail', id: decodeURIComponent(segments[1]) };
  }

  if (segments[0] === 'about') {
    return { name: 'about' };
  }

  if (segments[0] === 'compare') {
    const csv = params.get('id') ?? '';
    const ids = csv
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => decodeURIComponent(value));
    return { name: 'compare', ids, params };
  }

  return { name: 'list', params };
}

function handleRouteChange(): void {
  render();
}

function render(): void {
  const route = parseRoute();
  appRoot.innerHTML = '';

  if (route.name !== 'about') {
    if (STATE.loading) {
      appRoot.appendChild(renderLoading());
      return;
    }

    if (STATE.error) {
      appRoot.appendChild(renderError(STATE.error));
      return;
    }

    if (STATE.models.length === 0) {
      appRoot.appendChild(renderEmpty('No models available right now.', false));
      return;
    }
  }

  switch (route.name) {
    case 'list':
      document.title = 'Browse Models · AI Model Marketplace';
      appRoot.appendChild(renderList(route.params));
      break;
    case 'about':
      document.title = 'About · AI Model Marketplace';
      appRoot.appendChild(renderAbout());
      break;
    case 'detail': {
      const model = STATE.models.find((item) => item.id === route.id);
      if (model) {
        document.title = `${model.name} by ${model.provider} · AI Model Marketplace`;
        appRoot.appendChild(renderDetail(model));
      } else {
        document.title = 'Model not found · AI Model Marketplace';
        appRoot.appendChild(renderMissingModel(route.id));
      }
      break;
    }
    case 'compare':
      document.title = 'Compare Models · AI Model Marketplace';
      appRoot.appendChild(renderCompare(route.ids, route.params));
      break;
    default:
      document.title = 'AI Model Marketplace';
      appRoot.appendChild(renderList(new URLSearchParams()));
  }

  focusMain();
}

function renderAbout(): HTMLElement {
  const section = document.createElement('section');
  section.className = 'about-hero';

  const heading = document.createElement('h2');
  heading.className = 'about-title gradient-text';
  heading.textContent = 'About AI Model Marketplace';

  const lead = document.createElement('p');
  lead.className = 'about-lead';
  lead.textContent =
    'This site helps you discover, filter, and compare large language models across providers.';

  const list = document.createElement('ul');
  list.className = 'about-list';
  const bullets = [
    'Browse a curated catalog with rich metadata.',
    'Filter by provider, modality, and feature flags.',
    'Sort by update date, price, or context window.',
    'Compare up to three models side‑by‑side.',
  ];
  for (const item of bullets) {
    const li = document.createElement('li');
    li.textContent = item;
    list.append(li);
  }

  const cta = document.createElement('a');
  cta.href = '#/models';
  cta.className = 'button primary about-cta';
  cta.textContent = 'Start browsing models';

  section.append(heading, lead, list, cta);
  return section;
}

function renderLoading(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'loading-state';
  const heading = document.createElement('h2');
  heading.textContent = 'Loading models';
  const detail = document.createElement('p');
  detail.textContent = 'Fetching the latest catalog...';
  wrapper.append(heading, detail);
  return wrapper;
}

function renderError(message: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'error-state';
  const heading = document.createElement('h2');
  heading.textContent = 'Something went wrong';
  const detail = document.createElement('p');
  detail.textContent = message;
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'button primary';
  retry.textContent = 'Retry';
  retry.addEventListener('click', () => {
    void loadModels(true);
  });
  wrapper.append(heading, detail, retry);
  return wrapper;
}

function renderEmpty(message: string, offerReset: boolean): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'empty-state';
  const heading = document.createElement('h2');
  heading.textContent = 'No results';
  const detail = document.createElement('p');
  detail.textContent = message;
  wrapper.append(heading, detail);

  if (offerReset) {
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'button';
    clear.textContent = 'Clear filters';
    clear.addEventListener('click', () => resetListParams());
    wrapper.append(clear);
  }

  return wrapper;
}

function renderMissingModel(id: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'error-state';
  const heading = document.createElement('h2');
  heading.textContent = 'Model not found';
  const detail = document.createElement('p');
  detail.textContent = `We could not find a model with id "${id}".`;
  const action = document.createElement('a');
  action.href = '#/models';
  action.className = 'button primary';
  action.textContent = 'Back to browse';
  wrapper.append(heading, detail, action);
  return wrapper;
}

function renderList(params: URLSearchParams): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const providers = uniqueSorted(STATE.models.map((model) => model.provider));
  const modalities = uniqueSorted(
    STATE.models.flatMap((model) => model.modalities.map((value) => value.toLowerCase())),
  );
  const featureFlags = uniqueSorted(
    STATE.models.flatMap((model) => model.features.map((value) => value.toLowerCase())),
  );

  const listState = deriveListState(params);

  fragment.append(createControls(params, providers, modalities, featureFlags, listState.totalMatches));

  const summary = document.createElement('section');
  summary.className = 'results-summary';
  const headline = document.createElement('h2');
  headline.textContent = `Showing ${listState.visibleRangeText} of ${STATE.models.length} models`;
  const desc = document.createElement('p');
  desc.textContent = listState.summaryCopy;
  summary.append(headline, desc);
  fragment.append(summary);

  if (listState.pageModels.length === 0) {
    fragment.append(renderEmpty('Try adjusting search terms, filters, or clear them.', true));
    return fragment;
  }

  const grid = document.createElement('div');
  grid.className = 'model-grid';
  for (const model of listState.pageModels) {
    grid.append(createModelCard(model));
  }
  fragment.append(grid);

  fragment.append(createPagination(listState.currentPage, listState.totalPages));

  return fragment;
}

function deriveListState(params: URLSearchParams) {
  const searchTerm = (params.get('q') ?? '').trim().toLowerCase();
  const providerFilter = parseCsv(params.get('provider')).map((value) => value.toLowerCase());
  const modalityFilter = parseCsv(params.get('modality')).map((value) => value.toLowerCase());
  const featureFilter = parseCsv(params.get('flags')).map((value) => value.toLowerCase());
  const sortKey = parseSortKey(params.get('sort'));
  const pageParam = Number.parseInt(params.get('page') ?? '1', 10);

  const filtered = STATE.models.filter((model) => {
    if (searchTerm) {
      const haystack = [
        model.name,
        model.provider,
        ...model.tags,
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(searchTerm)) {
        return false;
      }
    }

    if (providerFilter.length > 0) {
      const providerMatched = providerFilter.includes(model.provider.toLowerCase());
      if (!providerMatched) {
        return false;
      }
    }

    if (modalityFilter.length > 0) {
      const modalities = model.modalities.map((value) => value.toLowerCase());
      for (const value of modalityFilter) {
        if (!modalities.includes(value)) {
          return false;
        }
      }
    }

    if (featureFilter.length > 0) {
      const features = model.features.map((value) => value.toLowerCase());
      for (const flag of featureFilter) {
        if (!features.includes(flag)) {
          return false;
        }
      }
    }

    return true;
  });

  const sorted = [...filtered].sort(createComparator(sortKey));
  const totalMatches = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalMatches / PAGE_SIZE));
  const currentPage = clampNumber(pageParam, 1, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const pageModels = sorted.slice(start, end);

  const visibleRangeText =
    totalMatches === 0
      ? '0'
      : `${start + 1}-${Math.min(end, totalMatches)}`;

  const summaryCopy = buildSummaryCopy(searchTerm, providerFilter, modalityFilter, featureFilter, totalMatches);

  return {
    filtered,
    pageModels,
    totalMatches,
    totalPages,
    currentPage,
    params,
    visibleRangeText,
    summaryCopy,
  };
}

function createControls(
  params: URLSearchParams,
  providers: string[],
  modalities: string[],
  featureFlags: string[],
  filteredCount: number,
): HTMLElement {
  const form = document.createElement('form');
  form.className = 'controls-panel';
  form.noValidate = true;
  form.addEventListener('submit', (event) => event.preventDefault());

  form.append(
    createSearchField(params.get('q') ?? ''),
    createProviderField(providers, params.get('provider')),
    createSortField(parseSortKey(params.get('sort'))),
    createCheckboxField('Modalities', 'modality', modalities, parseCsv(params.get('modality'))),
    createCheckboxField('Features', 'flags', featureFlags, parseCsv(params.get('flags'))),
  );

  const summary = document.createElement('p');
  summary.className = 'control-footnote';
  summary.textContent = `${filteredCount} model${filteredCount === 1 ? '' : 's'} match current filters.`;
  form.append(summary);

  const clearButton = document.createElement('button');
  clearButton.type = 'button';
  clearButton.className = 'button';
  clearButton.textContent = 'Clear filters';
  clearButton.addEventListener('click', () => resetListParams());
  form.append(clearButton);

  return form;
}

function createSearchField(initial: string): HTMLElement {
  const fieldset = document.createElement('fieldset');
  const legend = document.createElement('legend');
  legend.textContent = 'Search';
  const input = document.createElement('input');
  input.type = 'search';
  input.name = 'q';
  input.inputMode = 'search';
  input.placeholder = 'Search name, provider, or tags';
  input.value = initial;

  let handle: number | null = null;
  input.addEventListener('input', () => {
    if (handle !== null) {
      window.clearTimeout(handle);
    }
    handle = window.setTimeout(() => {
      const value = input.value.trim();
      updateListParams({ q: value || null }, { replace: true });
    }, 200);
  });

  fieldset.append(legend, input);
  return fieldset;
}

function createProviderField(providers: string[], selected: string | null): HTMLElement {
  const fieldset = document.createElement('fieldset');
  const legend = document.createElement('legend');
  legend.textContent = 'Provider';
  const select = document.createElement('select');
  select.name = 'provider';

  const defaultOption = document.createElement('option');
  defaultOption.value = '';
  defaultOption.textContent = 'All providers';
  select.append(defaultOption);

  for (const provider of providers) {
    const option = document.createElement('option');
    option.value = provider;
    option.textContent = provider;
    select.append(option);
  }

  if (selected) {
    select.value = selected;
  }

  select.addEventListener('change', () => {
    const value = select.value.trim();
    updateListParams({ provider: value || null });
  });

  fieldset.append(legend, select);
  return fieldset;
}

function createSortField(selected: SortKey): HTMLElement {
  const fieldset = document.createElement('fieldset');
  const legend = document.createElement('legend');
  legend.textContent = 'Sort by';
  const select = document.createElement('select');
  select.name = 'sort';

  const options: Array<{ value: SortKey; label: string }> = [
    { value: 'updated_at', label: 'Last updated (newest)' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'price_in', label: 'Input price (low-high)' },
    { value: 'price_out', label: 'Output price (low-high)' },
    { value: 'context', label: 'Context window (high-low)' },
  ];

  for (const option of options) {
    const opt = document.createElement('option');
    opt.value = option.value;
    opt.textContent = option.label;
    if (option.value === selected) {
      opt.selected = true;
    }
    select.append(opt);
  }

  select.addEventListener('change', () => {
    const value = select.value as SortKey;
    updateListParams({ sort: value }, { resetPage: false });
  });

  fieldset.append(legend, select);
  return fieldset;
}

function createCheckboxField(
  labelText: string,
  paramKey: 'modality' | 'flags',
  values: string[],
  selected: string[],
): HTMLElement {
  const fieldset = document.createElement('fieldset');
  const legend = document.createElement('legend');
  legend.textContent = labelText;

  const container = document.createElement('div');
  container.className = 'checkbox-grid';

  for (const value of values) {
    const id = `${paramKey}-${value}`;
    const label = document.createElement('label');
    label.htmlFor = id;
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = id;
    checkbox.name = paramKey;
    checkbox.value = value;
    checkbox.checked = selected.map((item) => item.toLowerCase()).includes(value.toLowerCase());
    checkbox.addEventListener('change', () => {
      const checkboxes = container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
      const picked = Array.from(checkboxes)
        .filter((node) => node.checked)
        .map((node) => node.value);
      updateListParams({ [paramKey]: picked.length > 0 ? picked : null });
    });
    label.append(checkbox, document.createTextNode(humanize(value)));
    container.append(label);
  }

  fieldset.append(legend, container);
  return fieldset;
}

function createPagination(currentPage: number, totalPages: number): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'pagination';
  nav.setAttribute('aria-label', 'Pagination');

  const status = document.createElement('span');
  status.textContent = `Page ${currentPage} of ${totalPages}`;

  const controls = document.createElement('div');
  controls.className = 'pagination-controls';

  const first = document.createElement('button');
  first.type = 'button';
  first.className = 'button';
  first.textContent = 'First';
  first.disabled = currentPage === 1;
  first.addEventListener('click', () => {
    updateListParams({ page: '1' }, { resetPage: false });
  });

  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'button';
  prev.textContent = 'Prev';
  prev.disabled = currentPage === 1;
  prev.addEventListener('click', () => {
    updateListParams({ page: String(currentPage - 1) }, { resetPage: false });
  });

  const jump = document.createElement('select');
  jump.ariaLabel = 'Jump to page';
  for (let page = 1; page <= totalPages; page += 1) {
    const option = document.createElement('option');
    option.value = page.toString();
    option.textContent = `Page ${page}`;
    if (page === currentPage) {
      option.selected = true;
    }
    jump.append(option);
  }
  jump.addEventListener('change', () => {
    updateListParams({ page: jump.value }, { resetPage: false });
  });

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'button';
  next.textContent = 'Next';
  next.disabled = currentPage === totalPages;
  next.addEventListener('click', () => {
    updateListParams({ page: String(currentPage + 1) }, { resetPage: false });
  });

  const last = document.createElement('button');
  last.type = 'button';
  last.className = 'button';
  last.textContent = 'Last';
  last.disabled = currentPage === totalPages;
  last.addEventListener('click', () => {
    updateListParams({ page: totalPages.toString() }, { resetPage: false });
  });

  controls.append(first, prev, jump, next, last);
  nav.append(status, controls);
  return nav;
}

function createModelCard(model: ModelRecord): HTMLElement {
  const article = document.createElement('article');
  article.className = 'model-card';

  const title = document.createElement('h2');
  const link = document.createElement('a');
  link.href = `#/models/${encodeURIComponent(model.id)}`;
  link.textContent = model.name;
  title.append(link);

  const meta = document.createElement('div');
  meta.className = 'meta-row';
  meta.append(
    createMetaChip('Provider', model.provider),
    createMetaChip('Context', formatContext(model.contextWindow)),
    createMetaChip('Updated', formatDate(model.updatedAt)),
  );

  const description = document.createElement('p');
  description.textContent = model.description;

  const pricing = document.createElement('div');
  pricing.className = 'metrics-bar';
  const inputLabel = createPricingPill('Input', model.pricing.input, model.pricing.currency);
  const outputLabel = createPricingPill('Output', model.pricing.output, model.pricing.currency);
  pricing.append(inputLabel, outputLabel);

  if (model.tags.length > 0) {
    const tags = document.createElement('div');
    tags.className = 'tags';
    for (const tag of model.tags) {
      const chip = document.createElement('span');
      chip.className = 'tag';
      chip.textContent = humanize(tag);
      tags.append(chip);
    }
    pricing.append(tags);
  }

  if (model.metrics && Object.keys(model.metrics).length > 0) {
    const metrics = document.createElement('div');
    metrics.className = 'metrics-bar';
    const subset = Object.entries(model.metrics).slice(0, 2);
    for (const [key, value] of subset) {
      const span = document.createElement('span');
      span.textContent = `${humanize(key)}: ${value}`;
      metrics.append(span);
    }
    article.append(metrics);
  }

  article.append(title, meta, description, pricing);

  const cta = document.createElement('a');
  cta.href = link.href;
  cta.className = 'button primary';
  cta.textContent = 'View details';
  article.append(cta);

  return article;
}

function renderDetail(model: ModelRecord): HTMLElement {
  const section = document.createElement('section');
  section.className = 'model-detail';

  const back = document.createElement('a');
  back.href = '#/models';
  back.className = 'button';
  back.textContent = 'Back to models';

  const header = document.createElement('div');
  header.className = 'detail-header';
  const title = document.createElement('h2');
  title.textContent = model.name;
  const provider = document.createElement('p');
  provider.className = 'meta-row';
  provider.textContent = `Provider · ${model.provider}`;
  header.append(title, provider);

  const description = document.createElement('p');
  description.textContent = model.description;

  const pricingGrid = document.createElement('div');
  pricingGrid.className = 'pricing-grid';
  pricingGrid.append(
    createPricingCard('Input tokens', model.pricing.input, model.pricing.currency),
    createPricingCard('Output tokens', model.pricing.output, model.pricing.currency),
  );

  const metaList = document.createElement('div');
  metaList.className = 'meta-row';
  metaList.append(
    createMetaChip('Context window', formatContext(model.contextWindow)),
    createMetaChip('Modalities', model.modalities.map(humanize).join(', ') || '—'),
    createMetaChip('Features', model.features.map(humanize).join(', ') || '—'),
    createMetaChip('Released', formatDate(model.releaseDate)),
    createMetaChip('Updated', formatDate(model.updatedAt)),
  );

  const compareLink = document.createElement('a');
  compareLink.href = `#/compare?id=${encodeURIComponent(model.id)}`;
  compareLink.className = 'button';
  compareLink.textContent = 'Compare this model';

  section.append(back, header, description, pricingGrid, metaList, compareLink);

  if (model.benchmarks && model.benchmarks.length > 0) {
    section.append(createBenchmarksTable(model.benchmarks));
  }

  if (model.links && Object.keys(model.links).length > 0) {
    const list = document.createElement('div');
    list.className = 'links';
    const heading = document.createElement('h3');
    heading.textContent = 'Useful links';
    const ul = document.createElement('ul');
    for (const [key, value] of Object.entries(model.links)) {
      if (!value) continue;
      const li = document.createElement('li');
      const anchor = document.createElement('a');
      anchor.href = value;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.textContent = humanize(key);
      li.append(anchor);
      ul.append(li);
    }
    list.append(heading, ul);
    section.append(list);
  }

  if (model.tags.length > 0) {
    const tagWrap = document.createElement('div');
    tagWrap.className = 'tags';
    for (const tag of model.tags) {
      const chip = document.createElement('span');
      chip.className = 'tag';
      chip.textContent = humanize(tag);
      tagWrap.append(chip);
    }
    section.append(tagWrap);
  }

  return section;
}

function createBenchmarksTable(entries: ModelBenchmark[]): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'benchmarks';
  const heading = document.createElement('h3');
  heading.textContent = 'Benchmarks';
  const table = document.createElement('table');
  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  const nameTh = document.createElement('th');
  nameTh.textContent = 'Benchmark';
  const scoreTh = document.createElement('th');
  scoreTh.textContent = 'Score';
  const unitTh = document.createElement('th');
  unitTh.textContent = 'Unit';
  headRow.append(nameTh, scoreTh, unitTh);
  thead.append(headRow);

  const tbody = document.createElement('tbody');
  for (const entry of entries) {
    const row = document.createElement('tr');
    const nameTd = document.createElement('td');
    nameTd.textContent = entry.name;
    const scoreTd = document.createElement('td');
    scoreTd.textContent = entry.score.toString();
    const unitTd = document.createElement('td');
    unitTd.textContent = entry.unit ?? (entry.higherIsBetter ? 'higher is better' : 'lower is better');
    row.append(nameTd, scoreTd, unitTd);
    tbody.append(row);
  }

  table.append(thead, tbody);
  wrapper.append(heading, table);
  return wrapper;
}

function renderCompare(ids: string[], params: URLSearchParams): HTMLElement {
  const wrapper = document.createElement('section');
  wrapper.className = 'compare-grid';

  const heading = document.createElement('h2');
  heading.textContent = 'Compare models';
  wrapper.append(heading);

  if (ids.length === 0) {
    const info = document.createElement('p');
    info.textContent = 'Add up to three model ids via #/compare?id=a,b,c to compare side by side.';
    wrapper.append(info);
    return wrapper;
  }

  const models = ids
    .map((id) => STATE.models.find((model) => model.id === id))
    .filter((model): model is ModelRecord => Boolean(model));

  if (models.length === 0) {
    wrapper.append(renderEmpty('Could not find the models you specified.', false));
    return wrapper;
  }

  if (models.length < ids.length) {
    const notice = document.createElement('p');
    notice.className = 'notice';
    notice.textContent = 'Some model ids were not recognized and were left out.';
    wrapper.append(notice);
  }

  const columns = document.createElement('div');
  columns.className = 'compare-columns';

  for (const model of models.slice(0, 3)) {
    const card = document.createElement('div');
    card.className = 'compare-card';
    const title = document.createElement('h3');
    title.textContent = model.name;
    const provider = document.createElement('p');
    provider.className = 'meta-row';
    provider.textContent = `Provider · ${model.provider}`;
    const context = document.createElement('p');
    context.textContent = `Context window: ${formatContext(model.contextWindow)}`;
    const pricing = document.createElement('div');
    pricing.className = 'metrics-bar';
    pricing.append(
      createPricingPill('Input', model.pricing.input, model.pricing.currency),
      createPricingPill('Output', model.pricing.output, model.pricing.currency),
    );

    if (model.benchmarks && model.benchmarks.length > 0) {
      const top = model.benchmarks[0];
      const bench = document.createElement('p');
      bench.textContent = `Top benchmark: ${top.name} (${top.score}${top.unit ? ` ${top.unit}` : ''})`;
      card.append(bench);
    }

    const link = document.createElement('a');
    link.href = `#/models/${encodeURIComponent(model.id)}`;
    link.className = 'button primary';
    link.textContent = 'View details';

    card.append(title, provider, context, pricing, link);
    columns.append(card);
  }

  wrapper.append(columns);

  if (params.get('id') === null) {
    const hint = document.createElement('p');
    hint.textContent = 'Use the compare link on model detail pages to add them here.';
    wrapper.append(hint);
  }

  return wrapper;
}

function createMetaChip(label: string, value: string | undefined): HTMLElement {
  const span = document.createElement('span');
  span.className = 'tag';
  span.textContent = value ? `${label}: ${value}` : `${label}: —`;
  return span;
}

function createPricingPill(label: string, perMillion: number | null, currency: string): HTMLElement {
  const pill = document.createElement('span');
  pill.className = 'pricing-pill';
  const formatted = formatPrice(perMillion, currency, 1_000_000);
  pill.textContent = `${label}: ${formatted.display}`;
  if (formatted.tooltip) {
    pill.title = formatted.tooltip;
  }
  return pill;
}

function createPricingCard(label: string, perMillion: number | null, currency: string): HTMLElement {
  const card = document.createElement('div');
  card.className = 'pricing-card';
  const heading = document.createElement('strong');
  heading.textContent = label;
  const million = document.createElement('span');
  const formatted = formatPrice(perMillion, currency, 1_000_000);
  million.textContent = formatted.display;
  if (formatted.tooltip) {
    million.title = formatted.tooltip;
  }
  card.append(heading, million);

  if (perMillion !== null) {
    const perThousand = document.createElement('span');
    const derived = formatPrice(perMillion / 1_000, currency, 1_000);
    perThousand.textContent = `${derived.display} per 1K`;
    if (derived.tooltip) {
      perThousand.title = derived.tooltip;
    }
    card.append(perThousand);
  }

  return card;
}

function buildSummaryCopy(
  searchTerm: string,
  providerFilter: string[],
  modalityFilter: string[],
  featureFilter: string[],
  total: number,
): string {
  const readableProviders = providerFilter.map(humanize);
  const readableModalities = modalityFilter.map(humanize);
  const readableFeatures = featureFilter.map(humanize);

  const parts: string[] = [];
  if (searchTerm) {
    parts.push(`matching "${searchTerm}"`);
  }
  if (readableProviders.length > 0) {
    parts.push(`from ${readableProviders.join(', ')}`);
  }
  if (readableModalities.length > 0) {
    parts.push(`covering ${readableModalities.join(', ')}`);
  }
  if (readableFeatures.length > 0) {
    parts.push(`with ${readableFeatures.join(', ')}`);
  }
  if (parts.length === 0) {
    return `${total} ready-to-use models.`;
  }
  return `${total} model${total === 1 ? '' : 's'} ${parts.join(', ')}.`;
}

function parseSortKey(value: string | null): SortKey {
  switch (value) {
    case 'name':
    case 'price_in':
    case 'price_out':
    case 'context':
    case 'updated_at':
      return value;
    default:
      return 'updated_at';
  }
}

function createComparator(sort: SortKey): (a: ModelRecord, b: ModelRecord) => number {
  switch (sort) {
    case 'name':
      return (a, b) => a.name.localeCompare(b.name);
    case 'price_in':
      return (a, b) => compareNullable(a.pricing.input, b.pricing.input);
    case 'price_out':
      return (a, b) => compareNullable(a.pricing.output, b.pricing.output);
    case 'context':
      return (a, b) => b.contextWindow - a.contextWindow;
    case 'updated_at':
    default:
      return (a, b) => {
        const aTime = Date.parse(a.updatedAt);
        const bTime = Date.parse(b.updatedAt);
        return Number.isNaN(bTime - aTime) ? 0 : bTime - aTime;
      };
  }
}

function compareNullable(a: number | null, b: number | null): number {
  const safeA = a ?? Number.POSITIVE_INFINITY;
  const safeB = b ?? Number.POSITIVE_INFINITY;
  if (safeA === safeB) {
    return 0;
  }
  return safeA < safeB ? -1 : 1;
}

function updateListParams(
  changes: Record<string, string | string[] | null>,
  options: { replace?: boolean; resetPage?: boolean } = {},
): void {
  const route = parseRoute();
  const params = route.name === 'list' ? new URLSearchParams(route.params) : new URLSearchParams();
  const replace = options.replace ?? false;
  const resetPage = options.resetPage ?? true;

  for (const [key, value] of Object.entries(changes)) {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        params.delete(key);
      } else {
        params.set(key, value.join(','));
      }
    } else if (value === null || value === '') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  }

  if (resetPage) {
    params.delete('page');
  }

  const query = params.toString();
  const hash = `#/models${query ? `?${query}` : ''}`;
  if (replace) {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
    render();
  } else {
    window.location.hash = hash;
  }
}

function resetListParams(): void {
  history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/models`);
  render();
}

function parseCsv(value: string | null): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatContext(tokens: number): string {
  if (!Number.isFinite(tokens)) {
    return '—';
  }
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M tokens`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(0)}K tokens`;
  }
  return `${tokens.toString()} tokens`;
}

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPrice(
  value: number | null,
  currency: string,
  denom: 1_000 | 1_000_000,
): { display: string; tooltip?: string } {
  if (value === null || !Number.isFinite(value)) {
    return { display: 'Not provided' };
  }

  const amount = denom === 1_000 ? value : value;
  const perUnit = denom === 1_000 ? '1K' : '1M';

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: amount < 1 ? 4 : 2,
    maximumFractionDigits: amount < 1 ? 6 : 2,
  });

  const displayValue = formatter.format(amount);
  if (amount < 0.0001) {
    const tooltip = `${displayValue} per ${perUnit} tokens`;
    return { display: `<${formatter.format(0.0001)}/${perUnit}`, tooltip };
  }

  return { display: `${displayValue} per ${perUnit}`, tooltip: `${displayValue} per ${perUnit} tokens` };
}

function clampNumber(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
}

async function loadModels(force = false): Promise<void> {
  if (!force && STATE.models.length > 0) {
    return;
  }
  STATE.loading = true;
  STATE.error = null;
  render();

  try {
    const response = await fetch(DATA_URL, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`);
    }
    const data = (await response.json()) as ModelRecord[];
    STATE.models = data;
    STATE.loading = false;
    STATE.error = null;
  } catch (error) {
    STATE.loading = false;
    STATE.error = error instanceof Error ? error.message : 'Unknown error while loading data.';
  }

  render();
}

function focusMain(): void {
  if (!(mainRegion instanceof HTMLElement)) {
    return;
  }
  window.requestAnimationFrame(() => {
    mainRegion.focus({ preventScroll: false });
  });
}

function initThemeControls(): void {
  const stored = (localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null) ?? 'auto';
  if (THEME_ORDER.includes(stored)) {
    currentTheme = stored;
  }
  applyTheme(currentTheme);

  themeToggleButton?.addEventListener('click', () => {
    const nextPreference = getNextTheme(currentTheme);
    currentTheme = nextPreference;
    applyTheme(currentTheme, true);
  });

  systemDarkQuery.addEventListener('change', () => {
    if (currentTheme === 'auto') {
      applyTheme('auto');
    } else {
      updateThemeButtonVisual();
    }
  });

  updateThemeButtonVisual();
}

function getNextTheme(current: ThemePreference): ThemePreference {
  const index = THEME_ORDER.indexOf(current);
  const nextIndex = (index + 1) % THEME_ORDER.length;
  return THEME_ORDER[nextIndex];
}

function applyTheme(mode: ThemePreference, persist = false): void {
  currentTheme = mode;
  document.body.dataset.theme = mode;
  if (persist) {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }
  updateThemeButtonVisual();
}

function updateThemeButtonVisual(): void {
  if (!themeToggleButton) {
    return;
  }
  const effectiveMode =
    currentTheme === 'auto' ? (systemDarkQuery.matches ? 'dark' : 'light') : currentTheme;
  themeToggleButton.dataset.mode = effectiveMode;
  const nextMode = getNextTheme(currentTheme);
  themeToggleButton.setAttribute(
    'aria-label',
    `Toggle color theme (next: ${nextMode})`,
  );
  themeToggleButton.title = `Theme: ${currentTheme} (next: ${nextMode})`;
}
